<script lang="ts" setup>
import type { RecipientGift } from '@consumer/types/serializers'
import GiftGuideBadge from './showcase/GiftGuideBadge.vue'
import GiftTipsBadge from './showcase/GiftTipsBadge.vue'
import Envelope from './Envelope.vue'
import Folio from './Folio.vue'
import ShowcaseRedeemOptions from './showcase/ShowcaseRedeemOptions.vue'
import ThankYouBadge from './showcase/ThankYouBadge.vue'
import ThankYouBubble from './showcase/ThankYouBubble.vue'

// Illustrative redemption finale for the demo/preview showcase. Reuses the
// envelope-rise + folio-emerge choreography from the recipient flow's
// RedemptionStage, but is driven step-by-step by `revealBeat` (the host's
// NavBar) rather than auto-timers, and has NO postOpenGift / view-details
// coupling — nothing here touches the backend or the real gift state.
//
// Beats:
//   7  → folio landed, cover closed (the plain giftly card)
//   8  → folio open ($amount reveal) + Gift Guide / Gift Tips badges
//   9  → folio open + Redeem Options overlay (badges hidden)
//   10 → folio open + thank-you bubble (demo only)
const { revealBeat } = defineProps<{
  gift?: RecipientGift
  mode: 'demo' | 'preview'
  revealBeat: number
  // Generic sender shown (without the "From" label) on the folio's bottom flap.
  senderName?: string
}>()

const folioOpen = $computed(() => revealBeat >= 8)

// Gift Guide / Gift Tips badges appear only once the folio has finished
// swinging open (the folio-swing runs ~1.2s after `open` flips true), so
// they don't pop in over a still-closed cover.
let badgesReady = $ref(false)
let badgeTimer: ReturnType<typeof setTimeout> | undefined
watch(() => folioOpen, (open) => {
  clearTimeout(badgeTimer)
  if (open) badgeTimer = setTimeout(() => { badgesReady = true }, 1300)
  else badgesReady = false
}, { immediate: true })

// Entrance: play the envelope-rise + folio-emerge on mount so the folio
// lands centered. Purely visual — no backend coupling.
let entered = $ref(false)
onMounted(() => nextTick(() => { entered = true }))
onBeforeUnmount(() => clearTimeout(badgeTimer))
</script>

<template>
  <div
    class="showcase-redemption-stage"
    :class="{ entered, 'shrink-folio': revealBeat === 8 || revealBeat === 9, finale: revealBeat >= 10 }"
  >
    <div class="envelope-stage">
      <Envelope :open="true" :fadeOut="folioOpen">
        <div class="flip-counter">
          <div class="folio-positioner">
            <Folio
              :gift="gift"
              :amount="gift?.amount"
              :open="folioOpen"
              :collapsed="revealBeat === 9"
              :finale="revealBeat >= 10"
              :senderName="senderName"
            />

            <!-- Beat 8: Gift Guide / Gift Tips badges. Anchored to the folio
                 (not the stage) so they hug its corners on any screen size,
                 and only shown once the folio has finished opening. -->
            <Transition name="fade-in-out">
              <div v-if="revealBeat === 8 && badgesReady" class="folio-badges">
                <GiftGuideBadge class="folio-badge folio-badge-guide"/>
                <GiftTipsBadge class="folio-badge folio-badge-tips"/>
              </div>
            </Transition>

            <!-- Beat 10 (demo only): thank-you finale — two THANK YOU stickers
                 hugging the giftly card's opposite corners (same folio-anchored
                 coordinate space as the beat-8 badges) so they track the card on
                 any screen size. -->
            <Transition name="fade-in-out">
              <div v-if="mode === 'demo' && revealBeat >= 10" class="folio-badges">
                <ThankYouBadge class="folio-badge folio-badge-thankyou"/>
                <div class="folio-thank-you-bubble">
                  <ThankYouBubble/>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </Envelope>
    </div>

    <!-- Beat 9: illustrative redemption options. -->
    <Transition name="fade-in-out">
      <div v-if="revealBeat === 9" class="overlay overlay-options">
        <ShowcaseRedeemOptions/>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.showcase-redemption-stage {
  // Per-beat downward nudge (set per beat below). Declared here so it inherits
  // down to `.flip-counter`, which consumes it as a composited translate.
  --folio-shift: 0;

  height: 100%;
  inset: 0;
  overflow: hidden;
  position: absolute;
  width: 100%;
}

// Envelope rises from off-screen to ~half visible, then the folio emerges
// from it and lands at viewport center. Mirrors RedemptionStage.
.envelope-stage {
  --scene-scale: min(0.8, calc((100vw - 32px) / 320));

  animation: envelope-rise 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  bottom: 0;
  left: 50%;
  position: absolute;
  transform: translate(-50%, 100%);

  :deep(.scene) {
    padding: 0 16px;
  }
}

@keyframes envelope-rise {
  to {
    transform: translate(-50%, 50%);
  }
}

.flip-counter {
  inset: 0;
  position: absolute;

  // The per-beat downward shift lives here as a GPU-composited translate — NOT
  // on `.folio-positioner`'s `top`. Animating `top` (a calc of % + a custom
  // property) triggers layout every frame and, on iOS/ProMotion, renders as a
  // stepped jump; a transform transition composites smoothly. It sits on this
  // static wrapper (rather than the positioner) so it doesn't fight the
  // folio-emerge animation's fill:forwards transform.
  transform: translateY(calc(var(--folio-shift) * 1px)) rotateY(180deg);
  transform-style: preserve-3d;
  transition: transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.folio-positioner {
  // Extra multiplier on the folio's resting scale, dropped below 1 on the
  // amount-reveal beat for short screens (see the max-height rule) so the whole
  // scene — including the $amount that floats above the card — shrinks and
  // clears the narration pill. Baked into the folio-emerge end frame so it
  // scales in place around the folio's center.
  --folio-scale: 1;

  left: 50%;
  opacity: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) rotate(40deg) scale(0.6);
  transition: opacity 0.4s ease-out;
  z-index: 5;
}

.entered .folio-positioner {
  animation: folio-emerge 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.5s forwards;
  opacity: 1;
}

@keyframes folio-emerge {
  0% {
    transform: translate(-50%, -50%) rotate(40deg) scale(0.6);
  }

  65% {
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transform: translate(-50%, calc(-30% - 40vh)) rotate(40deg) scale(0.9);
  }

  100% {
    transform: translate(-50%, calc(-30% - 40vh)) rotate(0deg) scale(calc(0.9 * var(--folio-scale)));
  }
}

// The thank-you finale (amount + giftly card + THANK YOU badge + message
// bubble) sits high with empty space below, so drop the whole scene a little.
.finale {
  --folio-shift: 40;
}

// Amount-showing beats (8 = reveal, 9 = redeem options): the $amount floats
// ~280px above the card, near the top of the scene, where it collides with the
// narration pill. Drop the whole scene by the same amount across both beats so
// the amount stays put (and clear of the pill) as beat 8 → 9. Mobile bumps it
// further below (see the media query).
.shrink-folio {
  --folio-shift: 36;
}

// On short screens the $amount that floats above the card collides with the
// narration pill, so we shrink the whole scene a touch. The scale is applied to
// the positioner un-gated (every beat, not just the amount-reveal one) so the
// folio *emerges* at this size and doesn't visibly pop smaller when it opens —
// the scale is baked into the emerge keyframe's forwards-fill transform, which
// can't be transitioned, so a per-beat change would snap.
@media (max-height: 899px) {
  .folio-positioner {
    --folio-scale: 0.9;
  }
}

// Mobile has more vertical room below the scene than beside the narration, so
// pull the whole scene lower than on desktop: further down on the amount-reveal
// beat, and further still on the thank-you finale.
@media (max-width: 1023px) {
  .shrink-folio {
    --folio-shift: 90;
  }

  .finale {
    --folio-shift: 90;
  }
}

// Badges live inside the folio-positioner so they track the folio itself
// (the box is the 320×220 folio; the opened cover extends ~220px above it,
// where the amount sits — hence the negative `top` offsets). This keeps them
// hugging the folio's corners regardless of viewport size.
.folio-badges {
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 10;
}

// All three badge SVGs are cropped tight to their 100-unit card with no baked
// shadow, so the soft float shadow lives here in CSS. drop-shadow (not
// box-shadow) so it hugs the card's rounded corners.
.folio-badge {
  display: block;
  filter: drop-shadow(0 22px 14px rgb(0 0 0 / 19%));
  height: auto;
  position: absolute;
  width: 115px;
}

// Upper-left, floating above the opened folio's top-left corner.
.folio-badge-guide {
  right: -58px;
  top: -247px;
}

// Right side, hugging the folio at the amount level.
.folio-badge-tips {
  left: -52px;
  top: 168px;
}

// Beat 10: THANK YOU sticker floating off the giftly card's top-left corner.
.folio-badge-thankyou {
  // Same spring as the thank-you illustration: the delay holds the small `from`
  // state through the parent's 0.3s fade so the pop lands on the now-opaque
  // sticker instead of being masked by the fade.
  animation: badge-pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.25s both;
  left: -65px;
  top: -230px;
  width: 100px;
}

@keyframes badge-pop-in {
  from {
    transform: translateY(10px) scale(0.85);
  }

  to {
    transform: translateY(0) scale(1);
  }
}

// Beat 10: recipient illustration, hugging the card's lower-right corner and
// spilling out to the right. Square artwork, so the width doubles as its height.
.folio-thank-you-bubble {
  bottom: 120px;
  pointer-events: none;
  position: absolute;
  right: -48px;
  width: 103.5px;
}

.overlay {
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 0 16px 120px;
  position: absolute;
  right: 0;
  z-index: 7;
}

// The redeem-options row sits in the upper-middle — below the $amount and
// above the (dropped) giftly card.
.overlay-options {
  align-items: flex-start;
  bottom: auto;
  padding-bottom: 0;
  top: 32%;

  // On desktop the amount sits lower/larger, so drop the options row down a
  // bit more to clear it.
  @media (min-width: 1024px) {
    top: 40%;
  }
}
</style>
